import BaseService from './BaseService';
import {
  ArchivingAuditLogListRequest,
  ArchivingAuditLogListResponse,
  ArchivingConversationMetadataResponse,
  ArchivingConversationResponse,
  ArchivingDeleteSavedSearchResponse,
  ArchivingExportDownloadResponse,
  ArchivingExportListResponse,
  ArchivingExportResponse,
  ArchivingFilterNode,
  ArchivingMessageAttachmentResponse,
  ArchivingMessageSearchResponse,
  ArchivingSavedSearchFilters,
  ArchivingSavedSearchListResponse,
  ArchivingSavedSearchResponse,
  ArchivingTypeaheadResponse,
} from '../apis/ArchivingAPI';

export default class ArchivingService extends BaseService {
  /**
   * Searches archived messages for an organization via the analytics-service message search endpoint.
   */
  async searchMessages(
    organizationId: string,
    query: string,
    options: {
      startDate?: string;
      endDate?: string;
      hasAttachment?: boolean;
      senderAccountIds?: string[];
      recipientAccountIds?: string[];
      filter?: ArchivingFilterNode;
      limit?: number;
    } = {}
  ): Promise<ArchivingMessageSearchResponse> {
    return this.host.api.archiving.searchMessages(organizationId, {
      orgId: organizationId,
      query,
      ...options,
    });
  }

  /**
   * Fetches every message in one conversation via the analytics-service conversation endpoint —
   * used by the "View Thread" action on a search result, keyed off that result's conversationId.
   */
  async getConversation(
    organizationId: string,
    conversationId: string,
    options: { limit?: number } = {}
  ): Promise<ArchivingConversationResponse> {
    return this.host.api.archiving.getConversation(organizationId, {
      orgId: organizationId,
      conversationId,
      ...options,
    });
  }

  /**
   * Fetches a conversation's load-once details (type, true message count) via the
   * analytics-service conversation metadata endpoint — separate from getConversation's message
   * list so it stays correct once that list paginates. See design doc 0002 §5.12.
   */
  async getConversationMetadata(
    organizationId: string,
    conversationId: string
  ): Promise<ArchivingConversationMetadataResponse> {
    return this.host.api.archiving.getConversationMetadata(organizationId, {
      orgId: organizationId,
      conversationId,
    });
  }

  /**
   * Queues an export for a list of message ids via the analytics-service export endpoint. The
   * caller resolves the export's source (selected messages, a full conversation thread, a saved
   * search) into messageIds beforehand.
   */
  async startExport(
    organizationId: string,
    options: {
      exportName?: string;
      messageIds: string[];
      includeAttachments?: boolean;
      notes?: string;
    }
  ): Promise<ArchivingExportResponse> {
    return this.host.api.archiving.startExport(organizationId, {
      orgId: organizationId,
      ...options,
    });
  }

  /**
   * Lists the caller's own export jobs, most recent first — powers the Export History tab. Never
   * every export in the org; an archiving_admin all-exports view is planned but not built.
   */
  async listExports(organizationId: string): Promise<ArchivingExportListResponse> {
    return this.host.api.archiving.listExports(organizationId, {
      orgId: organizationId,
    });
  }

  /**
   * Gets a short-lived presigned download URL for one of the caller's own ready export jobs.
   * Throws (via the underlying HTTP call) if the job isn't found, isn't ready yet, or has expired.
   */
  async getExportDownloadUrl(
    organizationId: string,
    jobId: string
  ): Promise<ArchivingExportDownloadResponse> {
    return this.host.api.archiving.getExportDownloadUrl(organizationId, {
      orgId: organizationId,
      jobId,
    });
  }

  /**
   * Fetches, decrypts, and returns a single archived message's attachment as a Blob + object URL
   * — the same {blob, url} shape MessagesService.downloadAttachmentBlobAndUrl returns for live
   * messages, so UI code (e.g. MessageAttachmentModal in web-react-ui-toolkit) doesn't need to
   * know whether an attachment is live or archived.
   */
  async getMessageAttachment(
    organizationId: string,
    messageId: string
  ): Promise<ArchivingMessageAttachmentResponse> {
    return this.host.api.archiving.getMessageAttachment(organizationId, {
      orgId: organizationId,
      messageId,
    });
  }

  /**
   * Prefix-searches users and roles by name for the Message Search UI's combined User/Role
   * autocomplete. A result's id is the same account token accepted by searchMessages' filter
   * tree (senderAccountId/recipientAccountId).
   */
  async searchUsersAndRoles(
    organizationId: string,
    query: string,
    options: { limit?: number } = {}
  ): Promise<ArchivingTypeaheadResponse> {
    return this.host.api.archiving.searchUsersAndRoles(organizationId, {
      orgId: organizationId,
      query,
      ...options,
    });
  }

  /** Saves the current Message Search filter state under a name, so it can be re-run later. */
  async createSavedSearch(
    organizationId: string,
    name: string,
    filters: ArchivingSavedSearchFilters
  ): Promise<ArchivingSavedSearchResponse> {
    return this.host.api.archiving.createSavedSearch(organizationId, {
      orgId: organizationId,
      name,
      filters,
    });
  }

  /**
   * Lists saved searches. Defaults to every saved search visible to the org; pass mine: true to
   * scope to the caller's own saved searches instead.
   */
  async getSavedSearches(
    organizationId: string,
    options: { mine?: boolean } = {}
  ): Promise<ArchivingSavedSearchListResponse> {
    return this.host.api.archiving.getSavedSearches(organizationId, {
      orgId: organizationId,
      ...options,
    });
  }

  /** Deletes a saved search. Only its creator may delete it — enforced server-side. */
  async deleteSavedSearch(
    organizationId: string,
    searchId: string
  ): Promise<ArchivingDeleteSavedSearchResponse> {
    return this.host.api.archiving.deleteSavedSearch(organizationId, {
      orgId: organizationId,
      searchId,
    });
  }

  /**
   * Reads one page of the org's archiving audit log, newest first — the Message Archive UI's audit
   * log view. Metadata only (message ids, counts, who/when), never message content. Requires
   * both view_archiving and archiving_admin in that org; the server enforces this and returns 403
   * otherwise; the UI can hide the entry point using UserRoles.isArchivingViewer and
   * UserRoles.isArchivingAdmin, but must not rely on that for security. Page with the previous response's
   * data.nextToken until it is null.
   */
  async getAuditLog(
    organizationId: string,
    options: Omit<ArchivingAuditLogListRequest, 'orgId'> = {}
  ): Promise<ArchivingAuditLogListResponse> {
    // orgId goes last so a stray orgId in options (JS callers aren't held to the Omit) can never
    // redirect the request to another org.
    return this.host.api.archiving.getAuditLog(organizationId, {
      ...options,
      orgId: organizationId,
    });
  }
}
